import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { recordCardTransaction } from "../../services/api/getPaymentCardFormToken";
import {
  updateUserPlan,
  updateUserPlanExpiration,
} from "../../actions/userActions";
import { useDispatch } from "react-redux";

export default function PayzenPaymentModal({
  open,
  onClose,
  formToken,
  userId,
  selectedPlan,
  onSuccess,
  onError,
}) {
  const [errorMessage, setErrorMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!open) return;

    setErrorMessage("");
    setIsSaving(false);

    if (!window.KR) {
      setErrorMessage(
        "PayZen engine is not initialized. Ensure script tags are added to index.html.",
      );
      return;
    }

    if (!formToken) return;

    let isMounted = true;

    const attachPayzenForm = async () => {
      try {
        await window.KR.setFormConfig({
          formToken: formToken,
          "kr-language": "en-US",
        });

        // Callback executed upon payment submission
        window.KR.onSubmit(async (paymentData) => {
          const status = paymentData.clientAnswer?.orderStatus;

          if (status === "PAID") {
            try {
              if (isMounted) setIsSaving(true);

              console.log("record transaction", selectedPlan);
              const selectedPlanName = selectedPlan?.title;
              // 1. Call backend to verify HMAC and save transaction
              const response = await recordCardTransaction({
                userId,
                selectedPlan: selectedPlanName,
                paymentData,
              });

              const activatedPlanId =
                response.data?.data?.plan_id || response.data?.planId;

              console.log("record", activatedPlanId);

              if (activatedPlanId) {
                dispatch(updateUserPlan(activatedPlanId));
              }
              const expirationDate =
                response.data?.data?.planExpiration ||
                response.data?.planExpiration;
              console.log("record", expirationDate);

              if (activatedPlanId) {
                dispatch(updateUserPlanExpiration(expirationDate));
              }

              if (response.data.status === "OK") {
                if (onSuccess) onSuccess(response.data);
              } else {
                throw new Error(
                  response.data.message || "Failed to record transaction.",
                );
              }
            } catch (err) {
              console.error("Backend verification error:", err);
              const apiError =
                err.response?.data?.message ||
                "Payment succeeded, but recording failed.";
              if (isMounted) setErrorMessage(apiError);
              if (onError) onError(err);
            } finally {
              if (isMounted) setIsSaving(false);
            }
          } else {
            if (onError) onError(paymentData);
          }

          return false; // Prevents default page redirect
        });

        window.KR.onError((error) => {
          console.error("PayZen Client Error:", error);
          if (isMounted) {
            setErrorMessage(
              error.errorMessage || "Payment error occurred. Please try again.",
            );
          }
          if (onError) onError(error);
        });
      } catch (err) {
        console.error("PayZen initialization failed:", err);
        if (isMounted) {
          setErrorMessage("Failed to load payment form.");
        }
      }
    };

    attachPayzenForm();

    return () => {
      isMounted = false;
      if (window.KR && typeof window.KR.removeForms === "function") {
        window.KR.removeForms();
      }
    };
  }, [open, formToken, userId, selectedPlan, onSuccess, onError]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs' fullWidth>
      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant='h6' fontWeight='bold'>
          Pay for Subscription
        </Typography>
        <IconButton onClick={onClose} size='small' disabled={isSaving}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 3, minHeight: 220 }}>
        {errorMessage && (
          <Alert severity='error' sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {(!formToken || isSaving) && !errorMessage && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
            }}
          >
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant='body2' color='text.secondary'>
              {isSaving
                ? "Verifying and recording transaction..."
                : "Initializing payment form..."}
            </Typography>
          </Box>
        )}

        {/* Embedded PayZen Container */}
        <Box
          sx={{
            display: formToken && !errorMessage && !isSaving ? "block" : "none",
            width: "100%",
          }}
        >
          <div className='kr-embedded' kr-form-token={formToken}>
            <button className='kr-payment-button'></button>
            <div className='kr-form-error'></div>
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
